<template>
  <ul>
    <v-card>
      <div class="card-header">
        <p class="card-header-title">
          <v-sheet>
            <v-btn @click="addSetting()">
              <span class="icon">
                <i class="mdi mdi-plus mdi-24px" />
              </span>
              <span>{{ $t('models.client.clientapp.addSetting') }}</span>
            </v-btn>
            <v-btn
              v-if="settingKinds.length === 0"
              class="-outline second-button"
              @click="addDefaultSettingKind()"
            >
              <span class="icon">
                <i class="mdi mdi-plus mdi-24px" />
              </span>
              <span>{{
                $t('models.client.clientapp.addDefaultSettingKind')
              }}</span>
            </v-btn>
          </v-sheet>
        </p>
      </div>
      <div class="card-content">
        <div v-for="(setting, index) in settings" :key="index">
          <setting-form
            :setting="setting"
            :object="clientApp"
            :setting-kinds="settingKinds"
            api-url="client/app"
            @delete-setting="deleteSetting"
            @save-setting="saveSettings"
          />
        </div>
      </div>
    </v-card>
  </ul>
  <LoadingOverlay v-model:loading="loading" size="64" color="primary" />
</template>

<script setup lang="ts">
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { onMounted, onUnmounted, Ref, ref } from 'vue'
import { useSnackbar } from 'vuetify-use-dialog'

import { apiClientV2, Context } from '@/api/ApiClientV2'
import errorHandler from '@/api/ErrorHandler'
import LoadingOverlay from '@/components/common/LoadingOverlay.vue'
import { createSuccessMessage } from '@/lang/setup'
import {
  CLIENT_APP_SETTING_DEFAULT,
  CLIENT_APP_SETTING_KIND_DEFAULT,
  ClientApp,
  ClientAppSetting,
  ClientAppSettingKind,
} from '@/models/client/models'
const createSnackbar = useSnackbar()

const props = defineProps({
  clientApp: {
    required: true,
    type: String,
  },
})

const settings: Ref<ClientAppSetting[]> = ref([])
const settingKinds: Ref<ClientAppSettingKind[]> = ref([])
const destroySubject = new Subject<void>()
const loading = ref(false)

onMounted(() => {
  getSettings()
  apiClientV2
    .getRefreshStream()
    .pipe(takeUntil(destroySubject))
    .subscribe(() => {
      getSettings()
    })
})

onUnmounted(() => {
  destroySubject.next()
  destroySubject.complete()
})

function saveSettings() {
  apiClientV2.refreshData()
}

async function getSettings() {
  loading.value = true
  try {
    settingKinds.value = await getSettingKinds()
    settings.value = await ClientApp.getSettings(apiClientV2, props.clientApp)
  } catch (error) {
    createSnackbar({
      text: errorHandler.errorToString(error),
      snackbarProps: { color: 'red-darken-3' },
    })
  }
  loading.value = false
}

async function deleteSetting(setting: ClientAppSetting) {
  const key = setting.key
  const id = setting.id
  const object_seq = setting.object_seq
  if (id === '0') {
    getSettings()
    return
  }
  loading.value = true
  try {
    await ClientApp.deleteSetting(apiClientV2, props.clientApp, key, object_seq)
    await getSettings()
  } catch (error) {
    createSnackbar({
      text: errorHandler.errorToString(error),
      snackbarProps: { color: 'red-darken-3' },
    })
  }
  loading.value = false
}

function addSetting() {
  settings.value.push(JSON.parse(JSON.stringify(CLIENT_APP_SETTING_DEFAULT)))
}

async function getSettingKinds(): Promise<ClientAppSettingKind[]> {
  try {
    const context: Context = {
      filter: {
        client_app: props.clientApp,
      },
      pagination: {},
    }
    return await apiClientV2.getListItems<ClientAppSettingKind>(
      ClientAppSettingKind,
      context
    )
  } catch (error) {
    console.warn(error)
    return Promise.resolve([])
  }
}

async function addDefaultSettingKind() {
  loading.value = true
  try {
    await apiClientV2.create(ClientAppSettingKind, {
      ...CLIENT_APP_SETTING_KIND_DEFAULT,
      name: 'default',
      handle: 'default',
      client_app: props.clientApp,
    })
    settingKinds.value = await getSettingKinds()
    createSnackbar({
      text: createSuccessMessage(ClientAppSettingKind),
      snackbarProps: { color: 'green-darken-3' },
    })
  } catch (error) {
    createSnackbar({
      text: errorHandler.errorToString(error),
      snackbarProps: { color: 'green-darken-3' },
    })
  }
  loading.value = false
}
</script>

<style lang="scss">
.second-button {
  margin-left: 15px;
}
</style>
